// https://documentation.bloomreach.com/engagement/docs/integration#configuration
export const SDK_SNIPPET_CONFIG_KEYS = [
  'target',
  'token',
  'file_path',
  'compliance',
  'new_experiments',
  'service_worker_path',
] as const;

// https://documentation.bloomreach.com/engagement/docs/configuration
export type SdkConfig = {
  token: string;
  target?: string;
  file_path?: string;
  compliance?: {
    opt_in: boolean;
  };
  new_experiments?:
    | boolean
    | {
        mode?: 'sync' | 'async';
        timeout?: number;
        hide_class?: string;
      };
  service_worker_path?: string;
  utm_always?: boolean;
  utm_params?: Array<string>;
  /**
   * @deprecated
   */
  track_hash_changes?: boolean;
  transfer_identity?: boolean;
  debug?: boolean;
  customer?: number | string | Record<string, string | number>;
  track?: {
    auto?: boolean;
    visits?: boolean;
    visits_query_params?: Array<string>;
    activity?: boolean;
    unloads?: boolean;
    exits?: boolean | (() => void);
    default_properties?: Record<string, any>;
    google_analytics?: boolean;
  };
  ping?: {
    enabled?: boolean;
    interval?: number;
    activity?: boolean | Record<string, any>;
    properties?: Record<string, any>;
  };
  cookies?: {
    cross_subdomain?: boolean | string;
    path?: string;
    retrieve_callback?: () => void;
    expires?:
      | number
      | Date
      | {
          tracking?: number | Date;
          ab_test?: number | Date;
          time?: number | Date;
        };
  };
  dependencies?: Record<string, string>;
  webOptimization?: boolean | Record<string, boolean>;
  push?: {
    safari: {
      websitePushID: string;
    };
  };
  spa_reloading?: {
    on_hash_change?: boolean;
    on_url_change?: boolean;
    banners?: boolean;
    experiments?: boolean;
    tags?: boolean;
    visits?: boolean;
    automatic_tracking?: boolean;
  };
};

export type SdkSnippetConfig = Pick<
  SdkConfig,
  (typeof SDK_SNIPPET_CONFIG_KEYS)[number]
>;

export type Api = {
  start: (config: Omit<SdkConfig, keyof SdkSnippetConfig>) => void;
  // https://documentation.bloomreach.com/engagement/docs/push-notifications
  notifications: {
    isAvailable: (callback: (available: boolean) => void) => void;
    isSubscribed: (
      callback: (active: boolean, denied: boolean) => void
    ) => void;
    subscribe: (
      callback?: (
        status:
          | 'error'
          | 'permission-denied'
          | 'subscribed'
          | 'default'
          | 'unsupported'
      ) => void,
      customerProperties?: Record<string, any>
    ) => void;
    unsubscribe: (
      callback?: (status: 'error' | 'unsubscribed' | 'unsupported') => void
    ) => void;
  };
  // https://documentation.bloomreach.com/engagement/docs/tracking
  track: (
    eventName: string,
    eventProperties: Record<string, any>,
    successCallback?: () => void,
    errorCallback?: () => void
  ) => void;
};

export type ContextValue = {
  initSdkOnce: (sdkConfig: SdkConfig) => void;
  api: Api | null;
};
